/**
 * StateCodeSelect - A dropdown for selecting US states that stores 2-letter codes.
 *
 * Features:
 * - Displays full state names (e.g., "New York")
 * - Stores 2-letter codes (e.g., "NY")
 * - Handles both code and name inputs gracefully
 * - Compatible with varchar(2) database fields
 */
import React from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { getStateCode, US_STATE_OPTIONS } from './constants';

interface StateCodeSelectProps {
  /** Current value - can be either 2-letter code or full name */
  value: string;
  /** Called with 2-letter state code when selection changes */
  onChange: (code: string) => void;
  /** Placeholder text when no selection */
  placeholder?: string;
  /** HTML id attribute */
  id?: string;
  /** ARIA label for accessibility */
  ariaLabel?: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether the select is disabled */
  disabled?: boolean;
}

export const StateCodeSelect: React.FC<StateCodeSelectProps> = ({
  value,
  onChange,
  placeholder = 'Select state',
  id = 'state-code-select',
  ariaLabel = 'State select',
  className = 'w-full',
  disabled = false,
}) => {
  // Normalize incoming value to code (handles both "NY" and "New York")
  const normalizedValue = value ? getStateCode(value) : '';

  // Find if the normalized value is a valid state code
  const isValidCode = US_STATE_OPTIONS.some((s) => s.code === normalizedValue);
  const displayValue = isValidCode ? normalizedValue : '';

  return (
    <Select value={displayValue} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} className={className} aria-label={ariaLabel}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {US_STATE_OPTIONS.map((state) => (
          <SelectItem key={state.code} value={state.code} className="text-sm">
            {state.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default StateCodeSelect;
